import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { LoginService } from './login.service';
import { AlertController } from '@ionic/angular';  // Add import
import { loadStripe, Stripe } from '@stripe/stripe-js';

@Injectable({
  providedIn: 'root'
})
export class WooCommerceService {
  private apiUrl = 'https://molhy.com/users/wp-json/wc/v3';
  private consumerKey = 'ck_40a12e1e7a5097b7bd1d0a4fa0c5445dc806aed1';
  private consumerSecret = 'cs_ace0cec649491346b40462f7b2863b4c1ea9a3ff';
  private apiCountry = 'https://restcountries.com/v3.1/all';
  private stripe: Stripe | null = null;
  private orderData: any = {
    discount_total: 0,
    line_items: [],
    // Add other necessary properties here
  };

  constructor(
    private http: HttpClient,
    private loginService: LoginService,
    private alertController: AlertController
  ) {
    this.initializeStripe();  // Initialize Stripe in the constructor
  }

  private async initializeStripe() {
    this.stripe = await loadStripe('pk_live_51PZSDnRodqiNA3jmGWsqdmSY86gmD8FUYig2KCamMq7OcVYcvIfUduVxb4HofSp5vSfmxUy9rFln1n95q4Qyh5wJ00AcO7Et1p');
  }

  async getStripeInstance(): Promise<Stripe | null> {
    if (!this.stripe) {
      await this.initializeStripe();
    }
    return this.stripe;
  } 

  private getAuthHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': 'Basic ' + btoa(`${this.consumerKey}:${this.consumerSecret}`)
    });
  }

  // Method to verify payment status using PaymentIntent ID
  verifyPayment(paymentIntentId: string): Observable<any> {
    const url = `https://molhy.com/users/wp-json/stripe/v1/verify-payment-intent/${paymentIntentId}`;

    return this.http.get(url).pipe(
      map((response: any) => response),
      catchError((error: HttpErrorResponse) => {
        return throwError(error);
      })
    );
  }

  // Step 1: Create the payment intent with MYR currency, supporting both card and FPX
  createPaymentIntent(amount: number, customerId: string, paymentMethodType: string = 'card'): Observable<any> {
    const url = 'https://molhy.com/users/wp-json/stripe/v1/create-payment-intent';

    return this.http.post(url, { 
      amount, 
      currency: 'myr', 
      customer_id: customerId,
      payment_method_types: [paymentMethodType]  // Allow specifying payment method type (card or fpx)
    });
  }

  createStripeCustomer(name: string, email: string): Observable<any> {
    const url = 'https://molhy.com/users/wp-json/stripe/v1/create-customer';
    return this.http.post(url, { name, email });
  }

  // Get Contest by WooCommerce ID
  getContestById(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/products/${id}`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((product: any) => {
        // Fetch custom fields from product meta_data
        const contestDate = product.meta_data.find((meta: any) => meta.key === '_contest_date')?.value;
        const contestStartTime = product.meta_data.find((meta: any) => meta.key === '_contest_start_time')?.value;
        const contestEndTime = product.meta_data.find((meta: any) => meta.key === '_contest_end_time')?.value;
        const contestIntroduction = product.meta_data.find((meta: any) => meta.key === '_contest_introduction')?.value;
        const contestObjectives = product.meta_data.find((meta: any) => meta.key === '_contest_objectives')?.value;
        const targetAudience = product.meta_data.find((meta: any) => meta.key === '_target_audience')?.value;
        const contestRules = product.meta_data.find((meta: any) => meta.key === '_contest_rules')?.value;
        const contestTerms = product.meta_data.find((meta: any) => meta.key === '_contest_terms')?.value;
        const contestPrizes = product.meta_data.find((meta: any) => meta.key === '_contest_prizes')?.value;
        const contestOrganizers = product.meta_data.find((meta: any) => meta.key === '_contest_organizers')?.value;

        // Return the contest with extracted meta data
        return {
          ...product,
          contestDate,
          contestStartTime,
          contestEndTime,
          contestIntroduction: contestIntroduction || 'No introduction available',
          contestObjectives: contestObjectives || 'No objectives available',
          targetAudience: targetAudience || 'No target audience specified',
          contestRules: contestRules || 'No rules available',
          contestTerms: contestTerms || 'No terms and conditions available',
          contestPrizes: contestPrizes || 'No prizes available',
          contestOrganizers: contestOrganizers || 'No organizers listed'
        };
      })
    );
  }

  // Get all contests
  getContests(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/products`, {
      headers: this.getAuthHeaders()
    }).pipe(
      map((products: any[]) => {
        return products.map(product => {
          // Fetch custom fields from product meta_data
          const contestDate = product.meta_data.find((meta: any) => meta.key === '_contest_date')?.value;
          const contestStartTime = product.meta_data.find((meta: any) => meta.key === '_contest_start_time')?.value;
          const contestEndTime = product.meta_data.find((meta: any) => meta.key === '_contest_end_time')?.value;
          const contestIntroduction = product.meta_data.find((meta: any) => meta.key === '_contest_introduction')?.value;
          const contestObjectives = product.meta_data.find((meta: any) => meta.key === '_contest_objectives')?.value;
          const targetAudience = product.meta_data.find((meta: any) => meta.key === '_target_audience')?.value;
          const contestRules = product.meta_data.find((meta: any) => meta.key === '_contest_rules')?.value;
          const contestTerms = product.meta_data.find((meta: any) => meta.key === '_contest_terms')?.value;
          const contestPrizes = product.meta_data.find((meta: any) => meta.key === '_contest_prizes')?.value;
          const contestOrganizers = product.meta_data.find((meta: any) => meta.key === '_contest_organizers')?.value;

          return {
            ...product,
            contestDate,
            contestStartTime,
            contestEndTime,
            contestIntroduction: contestIntroduction || 'No introduction available',
            contestObjectives: contestObjectives || 'No objectives available',
            targetAudience: targetAudience || 'No target audience specified',
            contestRules: contestRules || 'No rules available',
            contestTerms: contestTerms || 'No terms and conditions available',
            contestPrizes: contestPrizes || 'No prizes available',
            contestOrganizers: contestOrganizers || 'No organizers listed'
          };
        });
      })
    );
  }

  getCountries(): Observable<any> {
    return this.http.get(`${this.apiCountry}`).pipe(
      map((response: any) => {
        return response
          .map((country: any) => ({
            code: country.cca2,
            name: country.name?.common
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
      })
    );
  }

  getCountryNameByCode(code: string): Observable<string | null> {
    return this.getCountries().pipe(
      map((countries: { code: string, name: string }[]) => {
        const country = countries.find(country => country.code === code);
        return country ? country.name : null;
      })
    );
  }

  createOrder(orderData: any): Observable<any> {
    return from(this.loginService.getUserDetails()).pipe(
      switchMap(userDetails => {
        if (!userDetails || !userDetails.email) {
          throw new Error('User not authenticated or email not available');
        }

        orderData.billing.email = userDetails.email;
        orderData.customer_id = userDetails.id;
        orderData.set_paid = true;

        // Recalculate total price based on discount
        if (orderData.discount_total) {
          orderData.total = (orderData.line_items.reduce((sum: number, item: { price: number, quantity: number }) => sum + (item.price * item.quantity), 0) - orderData.discount_total).toFixed(2);
        } else {
          orderData.total = orderData.line_items.reduce((sum: number, item: { price: number, quantity: number }) => sum + (item.price * item.quantity), 0).toFixed(2);
        }
        
        return this.http.post(`${this.apiUrl}/orders`, orderData, {
          headers: this.getAuthHeaders()
        });
      }),
      map(response => response),
      catchError(error => throwError(error))
    );
  }

  getUserOrders(): Observable<any[]> {
    return from(this.loginService.getUserDetails()).pipe(
      switchMap(userDetails => {
        if (!userDetails || !userDetails.id) {
          return throwError('User not authenticated or user ID not available');
        }

        const url = `${this.apiUrl}/orders?customer=${userDetails.id}`;
        return this.http.get<any[]>(url, {
          headers: this.getAuthHeaders()
        });
      }),
      catchError((error: HttpErrorResponse) => throwError(error))
    );
  }

  // Define the presentAlert method in WooCommerceService
  async presentAlert(header: string, message: string) {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: ['OK'],
    });
    await alert.present();
  }

  //Create a method to validate and apply coupons
  validateCoupon(code: string): Observable<any> {
    const url = `${this.apiUrl}/coupons?code=${code}`;
    return this.http.get<any[]>(url, { headers: this.getAuthHeaders() }).pipe(
      map(coupons => {
        if (coupons.length > 0) {
          const coupon = coupons[0];
          this.orderData.discount_total = coupon.amount; // Set the discount amount in order data
          return coupon;
        }
        throw new Error('Invalid coupon code.');
      }),
      catchError((error: HttpErrorResponse) => throwError(error))
    );
  }

}